import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CertificateExpiryBadge, certificateExpiryBadge, certificateExpiryStatus } from './client-certificate.service';
import { instantRange, likeContains } from './frontdoor-v3-execution.service';
import { TimeWindow } from './frontdoor-v3-list-filters';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/**
 * A TLS server certificate of a v3 frontdoor. A TCP share that uses it presents it to clients in place of the platform
 * certificate, and answers at `hostname`. Immutable: to renew, upload a new one and move the shares to it.
 */
export interface ServerCertificateV3 {
    [key: string]: any;

    id: string;
    frontdoorId: string;
    name: string;
    /** The DNS name clients connect to. A DNS SAN of the leaf, in lowercase. */
    hostname: string;
    /** The name `hostname` must be a CNAME of. Derived by the service, not stored. */
    cnameTarget?: string;
    /** The certificate new shares get. At most one per account. */
    isDefault: boolean;
    arn?: string;
    commonName?: string | null;
    /** The DNS SANs of the leaf, in certificate order. */
    subjectAlternativeNames?: string[];
    issuer?: string | null;
    notBefore?: string;
    /** The earliest notAfter in the chain. */
    expiresAt?: string;
    /** SHA-256 of the leaf, as uppercase hex pairs joined by `:`. */
    fingerprintSha256?: string;
    /** For example `RSA 2048`, `EC P-256` or `Ed25519`. */
    keyAlgorithm?: string;
    /** The live TCP shares that use this certificate. */
    shareCount: number;
    /** The chain PEM. Only a single read, a create and an update carry it. */
    certificate?: string;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
}

/** A request to upload a server certificate. The service never returns the private key. */
export interface CreateServerCertificateV3 {
    name: string;
    /** One or more `CERTIFICATE` PEM blocks, leaf first. */
    certificate: string;
    /** One unencrypted private key PEM block that matches the leaf. */
    privateKey: string;
    /** Optional only when the leaf has exactly one DNS SAN and it is not a wildcard. */
    hostname?: string;
    isDefault?: boolean;
}

/** A request to change a server certificate. The certificate and the key cannot change. */
export interface UpdateServerCertificateV3 {
    name?: string;
    hostname?: string;
    isDefault?: boolean;
}

export interface ServerCertificatePageV3 {
    serverCertificates: ServerCertificateV3[];
    total: number;
}

/** Enough rows to read every certificate of an account in one call. The pages search and page the list locally. */
export const SERVER_CERTIFICATE_LIST_SIZE = 2000;

export const DEFAULT_SERVER_CERTIFICATE_PAGE_SIZE = 50;

/** What the listing asks the service to filter by. Each set field narrows the page further. */
export interface ServerCertificateListFilters {
    /** A case-insensitive contains. */
    name?: string;
    isDefault?: boolean;
    createdAt?: TimeWindow;
    updatedAt?: TimeWindow;
    expiresAt?: TimeWindow;
}

/** Adds `filters` to `params`. Text goes as a `LIKE` pattern, because the service adds no wildcards of its own. */
export function withServerCertificateFilters(
    params: HttpParams,
    filters: ServerCertificateListFilters = {}
): HttpParams {
    const name = (filters.name ?? '').trim();
    let withFilters = name ? params.set('name', likeContains(name)) : params;
    if (filters.isDefault !== undefined) {
        withFilters = withFilters.set('isDefault', String(filters.isDefault));
    }
    for (const field of ['createdAt', 'updatedAt', 'expiresAt'] as const) {
        const range = instantRange(filters[field]?.from, filters[field]?.to);
        if (range) {
            withFilters = withFilters.set(field, range);
        }
    }
    return withFilters;
}

/** The longest PEM the service accepts for the certificate chain or the private key. */
export const MAX_SERVER_CERTIFICATE_PEM_LENGTH = 32768;

/** True when the certificate has expired. A share cannot be moved to an expired certificate. */
export function isServerCertificateExpired(certificate: ServerCertificateV3 | undefined, now = Date.now()): boolean {
    return certificateExpiryStatus(certificate?.expiresAt, now) === 'expired';
}

/**
 * The badge {@link certificateExpiryBadge} draws, on the same warning window as a client certificate. Null while the
 * certificate is not yet expiring, or has no expiry.
 */
export function serverCertificateExpiryBadge(
    expiresAt: string | undefined,
    now = Date.now()
): CertificateExpiryBadge | null {
    const status = certificateExpiryStatus(expiresAt, now);
    return status === 'expired' || status === 'expiring' ? certificateExpiryBadge(expiresAt, now) : null;
}

/** Reads and writes the server certificates of one v3 frontdoor. Every path is scoped by the frontdoor id. */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3ServerCertificateService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /** Every server certificate of `frontdoorId`, newest first. A listing carries no `certificate`. */
    public findServerCertificates(
        frontdoorId: string,
        size = SERVER_CERTIFICATE_LIST_SIZE
    ): Observable<ServerCertificatePageV3> {
        const params = new HttpParams().set('sort', 'createdAt,DESC').set('size', String(size));
        return this.http
            .get(this.serverCertificatesUrl(frontdoorId), { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    /** One page of `frontdoorId`'s certificates, filtered and sorted by the service. `page` counts from 0. */
    public findServerCertificatePage(
        frontdoorId: string,
        sort = 'createdAt,DESC',
        size = DEFAULT_SERVER_CERTIFICATE_PAGE_SIZE,
        filters: ServerCertificateListFilters = {},
        page = 0
    ): Observable<ServerCertificatePageV3> {
        const params = withServerCertificateFilters(
            new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)),
            filters
        );
        return this.http
            .get(this.serverCertificatesUrl(frontdoorId), { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    /** One server certificate, with its chain PEM. */
    public getServerCertificate(frontdoorId: string, id: string): Observable<ServerCertificateV3> {
        return this.http
            .get(this.serverCertificateUrl(frontdoorId, id), { headers: this.headers() })
            .pipe(map((response: any) => response as ServerCertificateV3));
    }

    public createServerCertificate(
        frontdoorId: string,
        create: CreateServerCertificateV3
    ): Observable<ServerCertificateV3> {
        return this.http
            .post(this.serverCertificatesUrl(frontdoorId), create, { headers: this.headers() })
            .pipe(map((response: any) => response as ServerCertificateV3));
    }

    /** Whether {@link createServerCertificate} would be accepted. Sends the same body with the `nf-validate` header. */
    public validateCreateServerCertificate(
        frontdoorId: string,
        create: CreateServerCertificateV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.serverCertificatesUrl(frontdoorId), create);
    }

    /** Applies `update` with a `PATCH`. `isDefault: true` also clears the previous default. */
    public updateServerCertificate(
        frontdoorId: string,
        id: string,
        update: UpdateServerCertificateV3
    ): Observable<ServerCertificateV3> {
        return this.http
            .patch(this.serverCertificateUrl(frontdoorId, id), update, { headers: this.headers() })
            .pipe(map((response: any) => response as ServerCertificateV3));
    }

    /** Whether {@link updateServerCertificate} would be accepted. Uses `PATCH`, the same method as the write. */
    public validateUpdateServerCertificate(
        frontdoorId: string,
        id: string,
        update: UpdateServerCertificateV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.serverCertificateUrl(frontdoorId, id), update, 'PATCH');
    }

    /** Deletes the certificate. The service answers 409, with the shares named in the detail, while a live share uses it. */
    public deleteServerCertificate(frontdoorId: string, id: string): Observable<any> {
        return this.http.delete(this.serverCertificateUrl(frontdoorId, id), { headers: this.headers() });
    }

    private serverCertificatesUrl(frontdoorId: string): string {
        return `${this.apiUrl}/v3/${frontdoorId}/server-certificates`;
    }

    private serverCertificateUrl(frontdoorId: string, id: string): string {
        return `${this.serverCertificatesUrl(frontdoorId)}/${id}`;
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Spring HAL leaves `_embedded` out of an empty page, so its absence is an empty list. */
    private readPage(response: any): ServerCertificatePageV3 {
        const embedded = response?._embedded;
        const collection = embedded?.serverCertificates ?? (embedded ? Object.values(embedded)[0] : undefined);
        if (embedded && !Array.isArray(collection)) {
            this.logger.error('findServerCertificatePage: unrecognised response shape', response);
        }
        const rows = Array.isArray(collection) ? (collection as ServerCertificateV3[]) : [];
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : rows.length;
        return { serverCertificates: rows, total };
    }
}
